import {Match} from "@/modules/match/entity";
import {Team} from "@/modules/team/entity";
import {inject, injectable} from "inversify";
import {MatchResultEnum} from "@/modules/base/enums";
import {BankCategory} from "@/modules/finances/enums";
import {Division} from "@/modules/competitions/entity";
import KODivisionMatchGenerator from "@/modules/competitions/services/divisions/KODivisionMatchGenerator";
import {Season} from "@/modules/season/entity";
import {GateTypeEnum} from "@/modules/action-points/enums";
import GateManager from "@/modules/action-points/services/GateManager";

export interface PromotionEvaluator {
  evaluate(division: Division): void;
}

@injectable()
export default class KODivisionPromotionEvaluator implements PromotionEvaluator {
  constructor(
    @inject(KODivisionMatchGenerator) private koDivisionMatchGenerator: KODivisionMatchGenerator,
    @inject(GateManager) private gateManager: GateManager,
  ) {}

  async evaluate(division: Division): Promise<void> {
    division = await Division
      .createQueryBuilder('division')
      .innerJoinAndSelect('division.competition', 'competition')
      .leftJoinAndSelect('division.promotions', 'promotion')
      .innerJoinAndSelect('division.matchdays', 'matchday')
      .innerJoinAndSelect('matchday.season', 'season')
      .innerJoinAndSelect('matchday.matches', 'match')
      .innerJoinAndSelect('match.homeTeam', 'homeTeam')
      .innerJoinAndSelect('match.awayTeam', 'awayTeam')
      .leftJoinAndSelect('match.connectedMatch', 'connectedMatch')
      .where('division.id = :id AND season.current = true', {id: division.id})
      .getOneOrFail();

    if (division.matchdays.find(matchday => matchday.matches.find((match: Match) => !match.result))) {
      throw 'There are unfinished matches in this division';
    }

    const winners: Team[] = [];
    const homeMatchday = division.matchdays[0];

    for (const match of homeMatchday.matches) {
      let winner: Team;

      if (!match.connectedMatch) {
        switch (match.result) {
          case MatchResultEnum.HOME_WIN:
            winner = match.homeTeam;
            break;
          case MatchResultEnum.AWAY_WIN:
            winner = match.awayTeam;
            break;
          case MatchResultEnum.DRAW:
            winner = match.awayTeam;
            break;
        }

        match.advancedTeam = winner!;
        await match.save();

        continue;
      }

      const awayMatch = match.connectedMatch;
      const homeGoals = match.homeGoals! + awayMatch.awayGoals!;
      const awayGoals = match.awayGoals! + awayMatch.homeGoals!;

      if (homeGoals > awayGoals) {
        winner = match.homeTeam;
      } else if (homeGoals < awayGoals) {
        winner = match.awayTeam;
      } else {
        winner = match.awayTeam;
      }

      winners.push(winner);

      match.connectedMatch.advancedTeam = winner;
      await match.connectedMatch.save();

      match.advancedTeam = winner;
      await match.save();
    }

    const divisionPromotion = division.promotions[0];

    if (divisionPromotion) {
      if (divisionPromotion.price > 0 && !!winners.find(w => w.user)) {
        await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.add(
          divisionPromotion.price,
          {
            category: BankCategory.REWARDS,
            label: division.competition.name,
          }
        );
      }

      await divisionPromotion.load('target_division');
      const targetDivision = divisionPromotion.target_division;

      if (targetDivision) {
        targetDivision.teams = winners;
        await targetDivision.save();

        await this.koDivisionMatchGenerator.generate(await Season.current(), targetDivision);
      }
    }

    await division.save();
  }
}